import React, { useState, useEffect } from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import AxiosController from "../../../../authentication/AxiosController";
import Loader from "../../../../loader/Loader";

const Fulfillment = () => {
  const { slug, lang } = useParams();
  const [_] = useTranslation("global");
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [fulfillment, setFulfillment] = useState([]);
  const location = useLocation();
  const [userId, setUserId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [referredUserIds, setReferredUserIds] = useState([]);

  const [sortConfig, setSortConfig] = useState({
    key: "approved_payment_requests",
    direction: "descending",
  });

  const box_head_dir = lang === "en" ? "ltr" : "rtl";

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "0";
    const numValue = typeof amount === "string" ? parseFloat(amount) : amount;
    return isNaN(numValue) ? "0" : numValue.toLocaleString();
  };

  const formatReferredIds = (ids) => {
    if (!ids || !Array.isArray(ids)) return "-";
    return ids.join(", ");
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? " 🔼" : " 🔽";
    }
    return " ↕️";
  };

  const sortedData = React.useMemo(() => {
    let sortableItems = [...fulfillment];

    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === "user") {
          aValue = Array.isArray(aValue) ? aValue.length : 0;
          bValue = Array.isArray(bValue) ? bValue.length : 0;
        } else if (!isNaN(aValue) && !isNaN(bValue)) {
          aValue = Number(aValue);
          bValue = Number(bValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableItems;
  }, [fulfillment, sortConfig]);

  const get_fulfillment = () => {
    setLoading(true);
    AxiosController.get(`/api/${slug}/${lang}/reports/payments`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        role: "fulfillment",
        start_date: startDate,
        end_date: endDate,
        user_ids: referredUserIds,
        user_id: userId,
      },
    })
      .then((res) => {
        setFulfillment(res?.data || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    setUserId(urlParams.get("user_id") || "");
    setStartDate(urlParams.get("start_date") || "");
    setEndDate(urlParams.get("end_date") || "");
    const referredIds = urlParams.get("user_ids");
    setReferredUserIds(referredIds ? referredIds.split(",") : []);
  }, [location.search]);

  useEffect(() => {
    get_fulfillment();
  }, [userId, startDate, endDate, referredUserIds]);

  const handleReferredUserIdsFilter = (ids) => {
    if (!ids || !Array.isArray(ids)) return;
    const newUrlParams = new URLSearchParams(location.search);
    newUrlParams.set("user_ids", ids.join(","));
    window.history.pushState({}, "", `${location.pathname}?${newUrlParams.toString()}`);
    setReferredUserIds(ids);
  };

  const clearReferredUserIdsFilter = () => {
    const newUrlParams = new URLSearchParams(location.search);
    newUrlParams.delete("user_ids");
    window.history.pushState({}, "", `${location.pathname}?${newUrlParams.toString()}`);
    setReferredUserIds([]);
  };

  return (
    <div className="row" dir={box_head_dir} style={{ height: "1200px" }}>
      <Loader active={loading} />
      {referredUserIds.length > 0 && (
        <div className="mb-3 d-flex justify-content-end">
          <button
            className="btn btn-sm btn-danger"
            onClick={clearReferredUserIdsFilter}
          >
            {_("panel.clearUserIdsFilter") || "Clear Filter"}
          </button>
        </div>
      )}
      <div
        className="col-12"
        style={{ position: "relative", overflowX: "auto", height: "100%" }}
      >
        <Table
          responsive
          dir={box_head_dir}
          className="reports-table text-nowrap text-center"
        >
          <thead>
            <tr className="text-center">
              <th onClick={() => handleSort("id")} style={{ cursor: "pointer" }}>
                {_("home.fullname")}
                {getSortIndicator("id")}
              </th>
              <th onClick={() => handleSort("balance")} style={{ cursor: "pointer" }}>
                {_("panel.balance")}
                {getSortIndicator("balance")}
              </th>
              <th onClick={() => handleSort("confirmed_money")} style={{ cursor: "pointer" }}>
                {_("panel.confirmedMoney")}
                {getSortIndicator("confirmed_money")}
              </th>
              <th onClick={() => handleSort("expected_money")} style={{ cursor: "pointer" }}>
                {_("panel.expectedMoney")}
                {getSortIndicator("expected_money")}
              </th>
              <th onClick={() => handleSort("approved_payment_requests")} style={{ cursor: "pointer" }}>
                {_("panel.approvedPaymentRequests")}
                {getSortIndicator("approved_payment_requests")}
              </th>
              <th onClick={() => handleSort("sys_commission")} style={{ cursor: "pointer" }}>
                {_("panel.sys_commission")}
                {getSortIndicator("sys_commission")}
              </th>
              <th onClick={() => handleSort("referral_amount")} style={{ cursor: "pointer" }}>
                {_("panel.referral_link_value")}
                {getSortIndicator("referral_amount")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => (
              <tr key={index} className="text-center">
                <td>
                  <span className="fw-bold">#{item.id}</span>
                  <br />
                  {item.first_name} {item.last_name}
                  <br />
                  <span className="fw-bold ecombo-secondary">
                    {_("panel.fulfillment")}
                  </span>
                </td>
                <td>{formatAmount(item?.balance)}</td>
                <td>{formatAmount(item?.confirmed_money)}</td>
                <td>{formatAmount(item?.expected_money)}</td>
                <td>{formatAmount(item?.approved_payment_requests)}</td>
                <td>{formatAmount(item?.sys_commission)}</td>
                <td
                  onClick={() => handleReferredUserIdsFilter(item.referred_user_ids)}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {formatAmount(item?.referral_amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default Fulfillment;

